import React from 'react'

import {ReportRow} from '../../../types'
import copy from '../copy.json'
import {
  compareByName,
  daysSourceLabel,
  formatDaysSinceLastTag,
  formatTrained,
} from '../formatters'

interface Props {
  rows: ReportRow[]
  /** AC-8 — drives whether the bunk column exists at all, not whether it shows. */
  showBunkColumn: boolean
  /** The camp's own term for a bunk, title-cased. */
  bunkColumnHeading: string
  campName: string
  season: number
  /** Injected so the day counts are testable without freezing the clock. */
  now?: Date
}

/*
  The print target.

  On screen it is parked off-canvas rather than `display: none`, because a
  display-none subtree is not laid out and therefore does not print either. In
  the print stylesheet everything else on the page is hidden and this subtree is
  brought back to the top-left of the sheet.

  The bunk column is omitted from the DOM entirely when no bunk plan is selected
  (AC-8) — CSS-hiding it would leave it one stylesheet edit away from reappearing
  on paper with every cell empty.
*/
const PRINT_STYLES = `
@media screen {
  .tagged-photo-report-print-root {
    position: absolute;
    left: -10000px;
    top: 0;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }
}
@media print {
  body * { visibility: hidden; }
  .tagged-photo-report-print-root,
  .tagged-photo-report-print-root * { visibility: visible; }
  .tagged-photo-report-print-root {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: auto;
    overflow: visible;
  }
  .tagged-photo-report-print-root table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10pt;
  }
  .tagged-photo-report-print-root th,
  .tagged-photo-report-print-root td {
    border: 1px solid #999;
    padding: 2px 4px;
    text-align: left;
  }
  .tagged-photo-report-print-root thead { display: table-header-group; }
  .tagged-photo-report-print-root tr { page-break-inside: avoid; }
}
`

/*
  AC-25 / AC-26 — the figure and its label come from one call to one function, so
  a photo-derived number can never be rendered as a tag-derived one. Rendering
  them from two separate calls would make that a convention rather than a
  guarantee.
*/
const renderDaysCells = (
  row: ReportRow,
  now: Date | undefined,
): React.ReactElement => {
  const {days, source} = formatDaysSinceLastTag(
    row.taggedPhotoCount,
    row.lastTaggedUTC,
    row.lastTaggedPhotoCreatedUTC,
    now,
  )

  return (
    <>
      <td data-testid='tagged-photo-report-days-cell'>{days}</td>
      <td data-testid='tagged-photo-report-days-source-cell'>
        {daysSourceLabel(source)}
      </td>
    </>
  )
}

export const PrintableReport: React.FC<Props> = ({
  rows,
  showBunkColumn,
  bunkColumnHeading,
  campName,
  season,
  now,
}): React.ReactElement => {
  // AC-7. Sorted here rather than upstream so the printed artifact is ordered by
  // construction — no caller can hand this component an unordered set and get an
  // unordered page.
  const orderedRows = React.useMemo(() => [...rows].sort(compareByName), [rows])

  return (
    <div className='tagged-photo-report-print-root'>
      <style>{PRINT_STYLES}</style>

      <h1 data-testid='tagged-photo-report-heading'>
        {`${copy.title} — ${campName} — ${copy.report.seasonLabel} ${season}`}
      </h1>

      <table data-testid='tagged-photo-report-table'>
        <thead>
          <tr>
            <th>{copy.report.columns.lastName}</th>
            <th>{copy.report.columns.firstName}</th>
            <th>{copy.report.columns.personId}</th>
            <th>{copy.report.columns.sessions}</th>
            <th>{copy.report.columns.divisions}</th>
            {showBunkColumn && <th>{bunkColumnHeading}</th>}
            <th>{copy.report.columns.trained}</th>
            <th>{copy.report.columns.taggedPhotoCount}</th>
            <th>{copy.report.columns.daysSinceLastTag}</th>
            {/*
              AC-25 — unconditional, and immediately after the figure it
              qualifies rather than at the far edge of a ten-column row.
            */}
            <th>{copy.report.columns.daysSource}</th>
          </tr>
        </thead>
        <tbody>
          {orderedRows.map((row) => (
            <tr key={row.personId} data-testid='tagged-photo-report-row'>
              <td>{row.lastName}</td>
              <td>{row.firstName}</td>
              <td>{row.personId}</td>
              <td>{row.sessions}</td>
              <td>{row.divisions}</td>
              {showBunkColumn && (
                <td data-testid='tagged-photo-report-bunk-cell'>{row.bunk}</td>
              )}
              <td>{formatTrained(row.trained)}</td>
              <td>{row.taggedPhotoCount}</td>
              {renderDaysCells(row, now)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
